import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  INJECTOR,
  input,
  model,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  SearchResultRenderer,
  ShellSearchService,
  SOFT_SHELL_SEARCH_DISMISS,
} from '@softapps/core';
import { Application } from '@softline/application';
import { IconComponent, TranslatePipe } from '@softline/ui-core';
import { PinnedModulesService } from '../services/pinned-modules.service';

@Component({
  selector: 'soft-app-search-result',
  imports: [IconComponent, TranslatePipe],
  templateUrl: './app-search-result.html',
  styleUrl: './app-search-result.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(keydown.enter)': 'openApplication(this.result())',
  },
})
export class AppSearchResult extends SearchResultRenderer<Application> {
  override result = input.required<Application>();
  override highlighted = input(false);
  override disabled = model(false);

  readonly router = inject(Router);
  readonly injector = inject(INJECTOR);
  readonly overlay = inject(CdkConnectedOverlay);
  readonly shellSearchService = inject(ShellSearchService);

  readonly dismissSearchOverlay = inject(SOFT_SHELL_SEARCH_DISMISS);
  readonly pinnedService = inject(PinnedModulesService);

  readonly canPin = computed(() => {
    const app = this.result();
    return app?.type !== 'link' && !!app?.route;
  });

  readonly isPinned = computed(() => {
    const app = this.result();
    if (app?.type === 'link') return false;
    return this.pinnedService.isPinned(app?.route);
  });

  togglePin(event: MouseEvent): void {
    event.stopPropagation();
    const app = this.result();
    if (app?.type !== 'link' && app?.route) {
      this.pinnedService.toggle(app.route);
    }
  }

  openInNewTab(application: Application, event: MouseEvent): void {
    event.stopPropagation();
    if (application.type === 'link') {
      if (application.href) window.open(application.href, '_blank');
    } else if (application.route) {
      window.open(window.location.origin + application.route, '_blank');
    }
  }

  async openApplication(application: Application): Promise<void> {
    if (application?.type === 'link') {
      if (application?.['execute']) {
        await application.execute(this.injector);
      } else {
        window.open(application.href, '_blank');
      }
    } else {
      await this.router.navigate([application.route], application.routeParams);
    }

    await this.dismissSearchOverlay();
  }
}
